import { CoreEnums } from '@/utils/domain/constants';
import _ from 'lodash';

import evaluateFieldBehaviors from '../evaluateFieldBehaviors';

const { FormFieldType, FormFieldBehavior, FormFieldStringType, MappingValueSource } = CoreEnums;

const APPEARANCE_TYPES = {
  FILL_READ: 'fill-read',
};

const createRulesConfig = (required) => [{ required }];

const isLocationField = (fieldType) => {
  return fieldType === FormFieldType.Location || fieldType === FormFieldStringType.Location;
};

const getReadOnlyBehavior = (field) => ({
  readOnly: true,
  disabled: true,
  appearance: APPEARANCE_TYPES.FILL_READ,
  ...(!field?.shouldUpdate && {
    rules: createRulesConfig(isLocationField(field?.type) ? field?.required : false),
  }),
});

const getLockedBehavior = () => ({
  readOnly: true,
  disabled: true,
  isLocked: true,
  appearance: APPEARANCE_TYPES.FILL_READ,
  rules: createRulesConfig(false),
  style: { pointerEvents: 'none' },
  visibilityToggle: false,
  shouldUpdate: false,
  ariaDisabled: true,
  ariaReadonly: true,
});

export function getFieldInitialBehavior(field = {}) {
  const behaviorMap = {
    [FormFieldBehavior.ReadOnly]: () => getReadOnlyBehavior(field),
    [FormFieldBehavior.Hidden]: () => ({ hidden: true, rules: createRulesConfig(false) }),
    [FormFieldBehavior.Required]: () => ({ rules: createRulesConfig(true) }),
    [FormFieldBehavior.NotRequired]: () => ({ rules: createRulesConfig(false) }),
    [FormFieldBehavior.Locked]: getLockedBehavior,
  };

  const behaviorHandler = behaviorMap[field?.fieldBehavior];
  return behaviorHandler ? behaviorHandler() : {};
}

export const hasValidConditions = (behaviorCase) => {
  return (
    behaviorCase.conditionGroups?.length > 0 &&
    _.some(behaviorCase.conditionGroups, (cg) => cg.conditions?.length > 0)
  );
};

export function behaviorCasesByField(field, behaviorCases = []) {
  return _.filter(
    behaviorCases,
    (behaviorCase) => field?.id === behaviorCase.field?.id && hasValidConditions(behaviorCase),
  );
}

const LITERAL_SOURCES = [
  MappingValueSource.Variable,
  MappingValueSource.FixedValue,
  MappingValueSource.InternalField,
];

const isConditionResolvable = (condition) => {
  switch (condition.valueSource) {
    case MappingValueSource.FormField:
      return !condition.sourceField?.parentTable;
    case MappingValueSource.Formula:
      return false;
    default:
      return LITERAL_SOURCES.includes(condition.valueSource);
  }
};

const isCaseResolvable = (behaviorCase) =>
  _.every(behaviorCase.conditionGroups, (cg) =>
    _.every(cg.conditions, (c) => isConditionResolvable(c)),
  );

// Returns resolved `hidden`, or undefined when no case is resolvable (caller leaves the field as-is).
export function evaluateInitialHidden(conditionalCases, data) {
  const resolvable = _.filter(conditionalCases, isCaseResolvable);
  if (!resolvable.length) return undefined;

  const shim = { getFieldValue: (name) => data?.[name] };
  const result = evaluateFieldBehaviors.apply([shim, shim], [resolvable, undefined]);
  return result && 'hidden' in result ? result.hidden : undefined;
}

export const findApplicableBehavior = (behaviors) => {
  if (behaviors.length === 0) return null;
  if (behaviors.length === 1) {
    if (behaviors[0]?.conditionGroups?.some((cg) => cg.conditions?.length)) return null;
    return _.head(behaviors);
  }

  return (
    _.find(
      behaviors,
      (behaviorCase) =>
        !behaviorCase.conditionGroups?.length &&
        _.every(behaviorCase.conditionGroups, (cg) => !cg.conditions?.length),
    ) || null
  );
};

export const createBehaviorConfig = (field, behaviorCases, isLocked, shouldUpdate) => {
  if (isLocked || !shouldUpdate) return {};

  return {
    shouldUpdate: true,
    behaviorCases: behaviorCasesByField(field, behaviorCases),
  };
};

export { APPEARANCE_TYPES };
